import React, { memo, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';

import HelpIcon from 'components/common/icons/HelpIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@epoch-ui/components/Tooltip/tooltip';
import { LinkButton } from '@epoch-ui/components/LinkButton/LinkButton';
import { QUESTION_TYPE } from 'types/question.types';
import { PRIMARY_GREY } from 'constants/colours';

import NPSBreakdown from './NPSBreakdown';
import {
  InputDataPoint,
  ChartDataPoint,
  CHART_METADATA,
  calculateNPSBreakdown,
  createChartDataPoints,
  calculateAverage
} from './verticalBarChartUtil';

interface ChartProps {
  data: InputDataPoint[];
  properties: {
    scaleMinValue?: number;
    scaleMaxValue?: number;
    scaleMinLabel?: string;
    scaleMaxLabel?: string;
  };
  type?: QUESTION_TYPE;
}

interface BarLabelProps {
  x?: number;
  y?: number;
  width?: number;
  name?: string;
  data: ChartDataPoint[];
}

const BarLabel: React.FC<BarLabelProps> = ({ x = 0, y = 0, width = 0, name = '', data }) => {
  // Find the data point that matches the name
  const dataPoint = data.find((point) => point.name === name);
  if (!dataPoint) return null;

  return (
    <g>
      <text
        x={x + width / 2}
        y={y - 24}
        textAnchor="middle"
        className="tw-fill-primary-grey"
        fontSize="12"
      >
        {`${Math.round(dataPoint.percentage)}%`}
      </text>
      <text
        x={x + width / 2}
        y={y - 8}
        textAnchor="middle"
        className="tw-fill-primary-grey"
        fontSize="12"
      >
        {dataPoint.value}
      </text>
    </g>
  );
};

export const VerticalBarChart = memo(
  ({
    data,
    properties: { scaleMinValue = 1, scaleMaxValue = 5, scaleMinLabel, scaleMaxLabel },
    type = QUESTION_TYPE.LINEAR_SCALE
  }: ChartProps) => {
    const chartData = useMemo(() => {
      // Create an array of all possible values from minValue to maxValue
      const allValues = Array.from(
        { length: scaleMaxValue - scaleMinValue + 1 },
        (_, i) => i + scaleMinValue
      );

      // Create a map of value to count from the input data
      const valueMap = new Map(data.map((item) => [parseInt(item.name, 10), item]));

      // Calculate the average
      const average = calculateAverage(data, type);

      // Calculate NPS breakdown only if needed
      const npsBreakdown = type === QUESTION_TYPE.NPS ? calculateNPSBreakdown(data) : null;

      // Create the final chart data array with all possible values
      return {
        average,
        npsBreakdown,
        data: createChartDataPoints(allValues, valueMap)
      };
    }, [data, scaleMinValue, scaleMaxValue, type]);

    return (
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-1">
          <div className="tw-text-sm tw-font-normal tw-leading-5 tw-text-primary-grey">
            {CHART_METADATA[type]?.title ?? 'Average response'}
          </div>
          {type !== QUESTION_TYPE.LINEAR_SCALE && (
            <Tooltip>
              <TooltipTrigger className="tw-p-0">
                <HelpIcon color={PRIMARY_GREY} width="16px" height="16px" />
              </TooltipTrigger>
              <TooltipContent side="top" align="start" className="tw-w-[336px]">
                <div>
                  {CHART_METADATA[type]?.tooltip ?? ''}{' '}
                  <LinkButton
                    href="https://help.epochapp.com/articles/what-are-engagement-metrics"
                    target="_blank"
                    rel="noreferrer"
                    variant="link"
                    size="sm"
                    className="tw-h-4 tw-p-0 tw-text-xs tw-font-normal tw-leading-4 tw-text-white hover:tw-underline"
                  >
                    Learn more
                  </LinkButton>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="tw-text-[32px] tw-font-medium tw-leading-10">{chartData.average}</div>
        {type === QUESTION_TYPE.NPS && chartData.npsBreakdown && (
          <NPSBreakdown data={chartData.npsBreakdown} />
        )}
        <div className="tw-mt-2 tw-h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData.data}
              margin={{ top: 40, right: -20, left: -20, bottom: 8 }}
              barCategoryGap={20}
            >
              <CartesianGrid vertical={false} horizontal={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: PRIMARY_GREY, fontSize: 12 }}
                dy={8}
              />
              <YAxis hide domain={[0, 100]} />
              <Bar
                dataKey="blueHeight"
                className="tw-fill-primary-blue"
                radius={[4, 4, 4, 4]}
                stackId="stack"
              />
              <Bar
                dataKey="greyHeight"
                className="tw-fill-light-grey"
                radius={[4, 4, 0, 0]}
                stackId="stack"
                label={<BarLabel data={chartData.data} />}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="tw-flex tw-justify-between tw-text-xs tw-text-primary-grey">
          {scaleMinLabel && (
            <div className="tw-flex tw-flex-col tw-items-center">
              <span>{scaleMinLabel}</span>
            </div>
          )}
          {scaleMaxLabel && (
            <div className="tw-flex tw-flex-col tw-items-center">
              <span>{scaleMaxLabel}</span>
            </div>
          )}
        </div>
      </div>
    );
  }
);

VerticalBarChart.displayName = 'VerticalBarChart';
